import {
    type Touch,
    type TouchEvent,
    useCallback,
    useRef,
} from 'react'
import type {
    MorphingHtmlComponentPropsWithoutRef,
    ReactComponentOrTagName,
} from 'swayok-react-mdb-ui-kit/types/Common'

interface Props extends MorphingHtmlComponentPropsWithoutRef {
    onNext: () => void
    onPrevious: () => void
    // Минимальная длина жеста, в пикселях.
    minSwipeLength?: number
    // Минимальное смещение жеста по вертикали, в пикселях.
    maxVerticalDelta?: number
}

// Информация для жеста.
interface TouchGestureInfo {
    start: Touch | null
    end: Touch | null
}

// Компонент с функцией горизонтального свайпа для перелистывания чего-либо.
export function HorizontalSwiper(props: Props) {

    const {
        onNext,
        onPrevious,
        tag,
        minSwipeLength,
        maxVerticalDelta,
        onTouchStart,
        onTouchMove,
        onTouchEnd,
        onTouchCancel,
        children,
        ...otherProps
    } = props

    // Нам не нужны перерисовки из-за setState, поэтому храним данные в ref.
    const gestureInfo = useRef<TouchGestureInfo>({
        start: null,
        end: null,
    })

    const minHorizontalDiff: number = minSwipeLength || 50
    const maxVerticalDiff: number = maxVerticalDelta || 60
    const Tag: ReactComponentOrTagName = tag || 'div'

    // Обработчики свайпов.
    const onTouchStartHandler = useCallback((event: TouchEvent<HTMLElement>) => {
        if (event.targetTouches.length === 1) {
            gestureInfo.current.start = event.targetTouches[0]
        }
        onTouchStart?.(event)
    }, [onTouchStart])

    const onTouchMoveHandler = useCallback((event: TouchEvent<HTMLElement>) => {
        if (event.targetTouches.length > 0) {
            gestureInfo.current.end = event.targetTouches[0]
        }
        onTouchMove?.(event)
    }, [onTouchMove])

    const onTouchEndHandler = useCallback((event: TouchEvent<HTMLElement>) => {
        if (
            gestureInfo.current.start !== null
            && gestureInfo.current.end !== null
            // Жест должен иметь достаточное смещение по горизонтали в любую сторону.
            && Math.abs(gestureInfo.current.start.clientX - gestureInfo.current.end.clientX) > minHorizontalDiff
            // Жест не должен иметь сильное смещение по вертикали в любую сторону.
            && Math.abs(gestureInfo.current.start.clientY - gestureInfo.current.end.clientY) < maxVerticalDiff
        ) {
            if (gestureInfo.current.start.clientX < gestureInfo.current.end.clientX) {
                onPrevious()
            } else {
                onNext()
            }
        }
        onTouchEnd?.(event)
    }, [onPrevious, onNext, onTouchEnd])

    const onTouchCancelHandler = useCallback((event: TouchEvent<HTMLElement>) => {
        gestureInfo.current.start = null
        gestureInfo.current.end = null
        onTouchCancel?.(event)
    }, [])

    return (
        <Tag
            {...otherProps}
            onTouchStart={onTouchStartHandler}
            onTouchMove={onTouchMoveHandler}
            onTouchEnd={onTouchEndHandler}
            onTouchCancel={onTouchCancelHandler}
        >
            {children}
        </Tag>
    )
}
